import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Checkbox } from '@playbook/atoms/inputs/checkbox';
import { CheckboxGroup } from '@playbook/compositions/checkbox-group';

import { useCapabilities } from '@portal/pkg/api/keys';
import { LoadFailedInline } from '@portal/views/keys/LoadFailed';

import css from './CapabilityPicker.module.css';

interface CapabilityPickerProps {
	selected: string[];

	label: string;
	description?: string;
	help?: string;

	onChange: (capabilities: string[]) => void;
}

/**
 *	What a key may read.
 *
 *	Both the catalogue and the words for each entry come from the API. Each one
 *	is shown by its own name — memberRead, groupRead — and never relabelled: the
 *	name is what goes in the token request and what the documentation calls it.
 */
export function CapabilityPicker(props: CapabilityPickerProps): ReactNode {
	return (
		<ErrorBoundary
			fallback={<LoadFailedInline what="Could not load the access options." />}>
			<Suspense fallback={<p>Loading access options…</p>}>
				<Options {...props} />
			</Suspense>
		</ErrorBoundary>
	);
}

function Options({
	selected,

	label,
	description,
	help,

	onChange,
}: CapabilityPickerProps): ReactNode {
	const grantable = useCapabilities();

	function toggle(capability: string, checked: boolean): void {
		// Filtered out of the catalogue rather than appended, so the order the
		// API gave is the order that goes back to it.
		onChange(
			grantable
				.map(({ name }) => name)
				.filter((candidate) =>
					candidate === capability ? checked : selected.includes(candidate)
				)
		);
	}

	return (
		<div className={css.picker}>
			<CheckboxGroup
				name="capabilities"
				label={label}
				description={description}
				help={help}>
				{grantable.map((capability) => (
					<Checkbox
						key={capability.name}
						name="capabilities"
						value={capability.name}
						checked={selected.includes(capability.name)}
						label={capability.name}
						description={capability.description}
						onChange={(event) => toggle(capability.name, event.target.checked)}
					/>
				))}
			</CheckboxGroup>

			{/*
			 *	On screen rather than through CheckboxGroup's `help`, which
			 *	renders as the tooltip on the label's ⓘ — somewhere nobody
			 *	looks to find out why the button is disabled.
			 */}
			{selected.length === 0 && (
				<p className={css.required}>
					Pick at least one. A key with no access cannot be used.
				</p>
			)}
		</div>
	);
}
